import React from "react";
import ThemedModal from "../../utilities/ThemedModal";
import { MessageFilterForm } from "./form";
import { Button } from "../..";
import { Modal } from "react-bootstrap";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { FormValidationProvider, useFormValidation } from "./form-validation-context";
import { FormProvider, useForm } from "react-hook-form";
import { confirmable, createConfirmation } from "../../../hooks/DialogMountPoint";
import { MessageManagementDefaultFilter } from "../../../hooks/MessageManagementContext";
import { MessageFilterModalForm, MessageFilterModalProps } from "./types";
import { GridMessageFilters } from "../../../types/MessageType";

export const MessageFiltersModal = ({
    backdrop = 'static',
    cancel,
    centered = true,
    defaultFilters,
    defaultValues,
    dialogId,
    dismiss,
    level,
    proceed,
    show,
    size = "lg",
    ...rest
} : Readonly<MessageFilterModalProps>) => {
    const [bodyAnimation] = useAutoAnimate();
    const formMethods = useForm<MessageFilterModalForm>();
    const { handleSubmit, reset } = formMethods;
    const { isValid } = useFormValidation();

    const onClose = () => cancel!();

    const onClearFilters = () => proceed!({ ...defaultFilters, searchTerm: "" });

    const onSubmit = (form: MessageFilterModalForm) => proceed!(form);

    // whenever the default values change/we basically open the modal here.
    React.useEffect(() => {
        if (!defaultValues) {
            return;
        }
        reset({ ...defaultValues });
    }, [ defaultValues ]);

    return (
        <Modal backdrop={backdrop} centered={centered} size={size} show={show} onHide={onClose} {...rest}>
            <ThemedModal.Header>
                <Modal.Title>Message Filters</Modal.Title>
            </ThemedModal.Header>
            <FormProvider {...formMethods}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <Modal.Body ref={bodyAnimation}>
                        <MessageFilterForm isClientLevel={level === 'Client'} />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="warning" type="button" onClick={onClearFilters}>Clear Filters</Button>
                        <Button variant="success" type="submit" disabled={!isValid}>Apply Filters</Button>
                        <Button variant="light" type="button" onClick={onClose}>Cancel</Button>
                    </Modal.Footer>
                </form>
            </FormProvider>
        </Modal>
    );
};

export function openMessageFiltersModal(
    level: 'Adviser' | 'Client', 
    filters: GridMessageFilters,
    defaultFilters: GridMessageFilters | null = null,
    searchTerm: string | null = null
) {
    return createConfirmation<MessageFilterModalProps, MessageFilterModalForm>(confirmable((props) => (
        <FormValidationProvider>
            <MessageFiltersModal {...props} />
        </FormValidationProvider>
    )))({
        dialogId: 'document_filter_modal',
        defaultValues: { ...filters, searchTerm },
        defaultFilters: defaultFilters ?? MessageManagementDefaultFilter,
        level
    });
}